import { useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMeContext } from "../MeApp";
import type {
  OnboardingStep,
  OnboardingState,
  OnboardingAnswers,
} from "../types";
import { EMPTY_ONBOARDING_STATE } from "../types";
import { useSaveAnswers } from "../meHooks";
import { MAIN_FLOW_STEPS, STEP_BY_KEY, PROGRESS_BAR_KEYS } from "./flowConfig";

/** Router `location.state` shape `goBack` sets — see its own comment for why. */
export interface OnboardingNavState {
  navDirection?: "back";
}

/**
 * Hook orchestrating the onboarding flow.
 *
 * STORE UNIFICATION (landing plan §4): #938 kept TWO stores — the couple store
 * AND a standalone `dova:onboarding` localStorage layer (`persistence.ts`) that
 * could drift. That layer is gone. The per-partner `OnboardingState`'s durable
 * fields (`answers`, `consent`) live in the unified `me` store at
 * `couple.partners[i].onboardingState`, read via `useMeCouple()` (through
 * `useMeContext`). In-flight answers are written through `useSaveAnswers()`;
 * consent is now captured by the separate server-sealed e-sign ceremony.
 *
 * - URL `/me/onboarding/:step` is the source of truth for the current step.
 * - `furthestStep` is monotonic ephemeral nav state (local, like #938 — it was
 *   never load-bearing across partners). `step`/`completedAt` are likewise
 *   ephemeral nav state; only `answers` + `consent` round-trip the store.
 * - The public navigation/answer API remains from #938 (`state`, `currentStepKey`,
 *   `isValidStep`, `progressIndex`, `totalSteps`, `goNext`, `goBack`, `jumpTo`,
 *   `updateAnswers`, `markCompleted`) so the remaining step components
 *   need no changes.
 */
export function useOnboardingForm() {
  const { step } = useParams<{ step: string }>();
  const navigate = useNavigate();
  const { couple, partner } = useMeContext();
  const partnerId = partner?.id ?? null;

  // Durable answers/consent come straight from the unified store (couple →
  // this partner's embedded onboardingState). No second persistence layer.
  const storedState: OnboardingState = useMemo(() => {
    const fromStore = couple?.partners.find((p) => p.id === partnerId)
      ?.onboardingState;
    return fromStore ?? EMPTY_ONBOARDING_STATE;
  }, [couple, partnerId]);

  // Ephemeral nav state (monotonic furthestStep + completedAt) lives locally;
  // it isn't persisted in the sandbox store (matches #938: these were never
  // round-tripped through the couple store either).
  const [furthestStep, setFurthestStep] = useState<OnboardingStep>("welcome");
  const [completedAt, setCompletedAt] = useState<string | null>(null);

  const saveAnswers = useSaveAnswers(partnerId ?? "");

  const currentStepKey = (step as OnboardingStep) ?? "welcome";
  const isValidStep = !!STEP_BY_KEY[currentStepKey];
  const safeStepKey: OnboardingStep = isValidStep ? currentStepKey : "welcome";

  const mainFlowIndex = MAIN_FLOW_STEPS.findIndex((s) => s.key === safeStepKey);
  const progressIndex = PROGRESS_BAR_KEYS.indexOf(safeStepKey);
  const totalSteps = PROGRESS_BAR_KEYS.length;

  // The state object presented to step components. Durable fields from the
  // store; nav fields from local state + the URL.
  const state: OnboardingState = useMemo(
    () => ({
      step: safeStepKey,
      furthestStep,
      answers: storedState.answers,
      consent: storedState.consent,
      completedAt,
    }),
    [safeStepKey, furthestStep, storedState, completedAt]
  );

  // Preserve the monotonic furthestStep logic (MAIN_FLOW_STEPS index, so steps
  // outside the progress bar — e.g. homebase — still advance it correctly).
  const goNext = useCallback(() => {
    if (mainFlowIndex < 0 || mainFlowIndex >= MAIN_FLOW_STEPS.length - 1) return;
    const nextKey = MAIN_FLOW_STEPS[mainFlowIndex + 1].key;
    setFurthestStep((prev) => {
      const prevFurthestIdx = MAIN_FLOW_STEPS.findIndex((s) => s.key === prev);
      return mainFlowIndex + 1 > prevFurthestIdx ? nextKey : prev;
    });
    navigate(`/me/onboarding/${nextKey}`);
  }, [mainFlowIndex, navigate]);

  // `navDirection: "back"` rides in router location state, not the URL — it is
  // nav INTENT, not part of which step this is, and query/URL state is supposed
  // to be shareable/bookmarkable while this is neither. A step that treats
  // arrival as terminal in the forward direction (S8Consent: an already-signed
  // envelope immediately calls `onNext()`, so a client is never shown a sign
  // button for something already signed) needs to tell backward arrivals apart
  // from forward ones, or "back" bounces the client straight past it again in
  // the SAME direction it just tried to leave — see S8Consent.tsx's own use of
  // this flag for why.
  const goBack = useCallback(() => {
    const i = mainFlowIndex - 1;
    if (i < 0) return;
    navigate(`/me/onboarding/${MAIN_FLOW_STEPS[i].key}`, {
      state: { navDirection: "back" },
    });
  }, [mainFlowIndex, navigate]);

  const jumpTo = useCallback(
    (target: OnboardingStep) => {
      navigate(`/me/onboarding/${target}`);
    },
    [navigate]
  );

  const updateAnswers = useCallback(
    (patch: Partial<OnboardingAnswers>) => {
      if (!partnerId) return Promise.resolve();
      // Send the RAW partial patch — the server merges it onto the stored answers
      // (server-authoritative). Building `{...storedState.answers, ...patch}` here
      // could clobber a prior field when two quick step submits each merge onto a
      // stale react-query snapshot. The invalidate → refetch brings the merged
      // state back into `storedState` for display (preview, etc.).
      return saveAnswers.mutateAsync(patch);
    },
    [partnerId, saveAnswers]
  );

  const markCompleted = useCallback(() => {
    setCompletedAt(new Date().toISOString());
  }, []);

  return {
    state,
    currentStepKey: safeStepKey,
    isValidStep,
    progressIndex,
    totalSteps,
    goNext,
    goBack,
    jumpTo,
    updateAnswers,
    markCompleted,
  };
}
